/**
 * LeadProfileSlideIn.jsx
 * ──────────────────────────────────────────────────────────
 * Right-side slide-in lead profile that appears during an active
 * call on /dialer/live (Phase C · Section 3). Lets the rep glance
 * at the lead's vitals, AI fit, talking points, recent activity,
 * and inline-edit contact/address fields — without leaving the call.
 *
 * v2 (Feb 12) — Now also accessible from the "Lead info" button next
 * to Email in the contacts card. Collapsible into a 56px vertical
 * rail. Snapshot tab carries 16+ data points. Quick edit covers
 * ALL contacts + ALL addresses (not just primary).
 */
import React, { useMemo, useState, useEffect } from "react";
import { toast } from "sonner";
import { getLeadScore } from "@/lib/getLeadScore";
import QuickEditLeadFields from "@/components/dialer/QuickEditLeadFields";
import {
  X, ExternalLink, Phone, Mail, Sparkles, Activity as ActivityIcon,
  TrendingUp, MapPin, Edit3, Check, Tag, Clock, ChevronRight, ChevronLeft,
  Star, Building2, Users, Briefcase, Award, Calendar as CalendarIcon,
  DollarSign, Stethoscope, Cpu, Trash2, Plus, User as UserIcon,
} from "lucide-react";

const COLLAPSED_W = 56;
const EXPANDED_W  = 480;
const STORAGE_KEY = "leadProfileCollapsed";

const fmtUSD = (n) => (typeof n === "number" ? `$${n.toLocaleString()}` : "—");
const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString() : "—");
const initials = (name) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

function daysSince(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.round(ms / 86400000));
}

export default function LeadProfileSlideIn({ open, lead, onClose, onOpenLead, onChangeLead, onCall, onEmail, onFollowUp }) {
  const [tab, setTab] = useState("snapshot");
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  useEffect(() => { if (open) setTab("snapshot"); }, [open, lead?.id]);

  // ── Derived stats ──────────────────────────────────────────
  const stats = useMemo(() => {
    if (!lead) return null;
    const m = lead.medicare || {};
    const top1 = (lead.top_hcpcs || [])[0];
    const top2 = (lead.top_hcpcs || [])[1];
    const top3 = (lead.top_hcpcs || [])[2];
    const lastTouch = lead.activity?.[0]?.at || lead.recency?.last_touch_at;
    const nextAction = lead.next_action;
    const fit = lead.product_fit || lead.ai_fit_breakdown || lead.fit_scores || {};
    return {
      benes: m.beneficiaries || 0,
      allowed: m.allowed || 0,
      medicarePartB: m.partB || m.beneficiaries_partB || null,
      medicareAdvantage: m.medicare_advantage || m.ma_pct || null,
      top1, top2, top3,
      ehrVendor: lead.ehr?.vendor || "(none)",
      ehrProduct: lead.ehr?.product || "—",
      ehrAge: lead.ehr?.years_on_system || lead.ehr?.contract_age || null,
      aiScore: getLeadScore(lead),
      providers: lead.practice?.group_size || lead.practice?.providers || 1,
      patients: lead.practice?.active_patients || lead.demographics?.total_patients || null,
      avgAge: lead.demographics?.average_age || null,
      femalePct: lead.demographics?.female_pct || null,
      specialty: lead.nppes?.specialty_group || lead.nppes?.specialty || lead.specialty || "—",
      subSpecialty: lead.nppes?.sub_specialty || null,
      taxonomy: lead.nppes?.taxonomy || null,
      stage: lead.stage || "new",
      subStatus: lead.subStatus || null,
      tier: lead.tier || "—",
      source: lead.source || lead.lead_source || "—",
      rating: lead.rating || lead.lead_rating || null,
      owner: lead.owner || lead.assigned_to || null,
      daysInPipeline: daysSince(lead.created_at) ?? daysSince(lead.first_touch_at),
      daysSinceTouch: daysSince(lastTouch),
      lastTouchAt: lastTouch,
      nextAction,
      fit: {
        ccm:  fit.CCM  ?? fit.ccm,
        rpm:  fit.RPM  ?? fit.rpm,
        apcm: fit.APCM ?? fit.apcm,
        awv:  fit.AWV  ?? fit.awv,
        ehr:  fit.EHR  ?? fit.ehr,
        rcm:  fit.RCM  ?? fit.rcm,
      },
      revenueEstimate: lead.revenue_estimate || lead.estimated_arr || (m.allowed ? Math.round(m.allowed * 0.18) : null),
      decisionTimeline: lead.prospect_decision_timeline || lead.timeline,
      budgetRange: lead.budget_range,
      painPoint: lead.pain_point_category || lead.pain_points,
    };
  }, [lead]);

  if (!open || !lead) return null;

  const contacts  = lead.contacts  || [];
  const locations = lead.locations || [];

  const updateContact = (idx, patch) => {
    if (!onChangeLead) return;
    onChangeLead({ ...lead, contacts: contacts.map((c, i) => i === idx ? { ...c, ...patch } : c) });
  };
  const removeContact = (idx) => {
    if (!onChangeLead) return;
    if (!confirm(`Remove ${contacts[idx]?.name || "contact"}?`)) return;
    onChangeLead({ ...lead, contacts: contacts.filter((_, i) => i !== idx) });
    // silent-save:ok parent <Leads> persists via axios.put on every onChangeLead
    toast.success("Contact removed");
  };
  const addContact = () => {
    if (!onChangeLead) return;
    onChangeLead({ ...lead, contacts: [...contacts, { id: `c-${Date.now()}`, name: "", role: "", phone: "", email: "" }] });
  };
  const updateAddress = (idx, patch) => {
    if (!onChangeLead) return;
    onChangeLead({ ...lead, locations: locations.map((a, i) => i === idx ? { ...a, ...patch } : a) });
  };
  const removeAddress = (idx) => {
    if (!onChangeLead) return;
    if (!confirm(`Remove this address?`)) return;
    onChangeLead({ ...lead, locations: locations.filter((_, i) => i !== idx) });
    // silent-save:ok parent <Leads> persists via axios.put on every onChangeLead
    toast.success("Address removed");
  };
  const addAddress = () => {
    if (!onChangeLead) return;
    onChangeLead({ ...lead, locations: [...locations, { id: `a-${Date.now()}`, label: "Office", address: "", city: "", state: "", zip: "" }] });
  };

  // ── Collapsed rail (vertical strip with icons) ─────────────
  if (collapsed) {
    return (
      <aside data-testid="lead-profile-rail" style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: COLLAPSED_W,
        background: "var(--paper, #fff)", borderLeft: "0.5px solid var(--border-soft, #eee)",
        boxShadow: "-4px 0 12px rgba(15,23,42,0.06)", zIndex: 65,
        display: "flex", flexDirection: "column", animation: "lp-slide 220ms cubic-bezier(0.4,0,0.2,1)",
      }}>
        <button
          data-testid="lp-expand"
          onClick={() => setCollapsed(false)}
          title="Expand lead profile"
          style={{
            border: "none", background: "var(--blue-soft, #EEF2FF)",
            padding: "12px 0", cursor: "pointer", color: "var(--blue, #5B6CFF)",
            borderBottom: "0.5px solid var(--border-soft)",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
          }}>
          <ChevronLeft style={{ width: 14, height: 14 }} />
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", writingMode: "vertical-rl", transform: "rotate(180deg)", marginTop: 4 }}>EXPAND</span>
        </button>

        {/* Mini avatar */}
        <div style={{ padding: "10px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, borderBottom: "0.5px solid var(--border-soft)" }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--navy, #0F1E3D)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600 }}>
            {initials(lead.clinic)}
          </div>
          <div data-testid="lp-rail-score" style={{ fontSize: 10, fontWeight: 700, color: stats.aiScore >= 71 ? "#0F6E56" : stats.aiScore >= 41 ? "#A6660B" : "var(--text-3)" }}>
            {stats.aiScore}
          </div>
        </div>

        {/* Tab icons */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", paddingTop: 6 }}>
          {[
            { id: "snapshot", Icon: TrendingUp,   label: "Snapshot" },
            { id: "talking",  Icon: Sparkles,     label: "Talking pts" },
            { id: "activity", Icon: ActivityIcon, label: "Activity" },
            { id: "edits",    Icon: Edit3,        label: "Quick edit" },
          ].map((t) => {
            const Icon = t.icon || t.Icon;
            return (
              <button
                key={t.id}
                title={t.label}
                data-testid={`lp-rail-${t.id}`}
                onClick={() => { setTab(t.id); setCollapsed(false); }}
                style={{
                  padding: "12px 0", background: tab === t.id ? "var(--blue-soft)" : "transparent",
                  border: "none", cursor: "pointer", color: tab === t.id ? "var(--blue)" : "var(--text-3)",
                  display: "flex", justifyContent: "center", borderLeft: tab === t.id ? "2px solid var(--blue)" : "2px solid transparent",
                }}>
                <Icon style={{ width: 14, height: 14 }} />
              </button>
            );
          })}
        </div>

        <button
          data-testid="lp-rail-close"
          onClick={onClose}
          title="Close panel"
          style={{ border: "none", borderTop: "0.5px solid var(--border-soft)", background: "var(--subtle)", padding: "10px 0", cursor: "pointer", color: "var(--text-3)" }}>
          <X style={{ width: 13, height: 13 }} />
        </button>

        <style>{`
          @keyframes lp-slide { from { transform: translateX(100%); } to { transform: translateX(0); } }
        `}</style>
      </aside>
    );
  }

  // ── Expanded panel ─────────────────────────────────────────
  const primary = contacts[0];

  return (
    <>
      <div data-testid="lead-profile-backdrop" onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.28)",
        zIndex: 60, animation: "lp-fade 180ms ease",
      }} />

      <aside data-testid="lead-profile-panel" style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: EXPANDED_W, maxWidth: "94vw",
        background: "var(--paper, #fff)", borderLeft: "0.5px solid var(--border-soft, #eee)",
        boxShadow: "-12px 0 32px rgba(15,23,42,0.18)", zIndex: 65,
        display: "flex", flexDirection: "column", animation: "lp-slide 240ms cubic-bezier(0.4,0,0.2,1)",
      }}>

        {/* Header */}
        <div style={{ padding: "14px 16px", borderBottom: "0.5px solid var(--border-soft)", display: "flex", alignItems: "flex-start", gap: 10 }}>
          <button
            data-testid="lp-collapse"
            onClick={() => setCollapsed(true)}
            title="Collapse to rail"
            style={{ background: "var(--subtle)", border: "0.5px solid var(--border-soft)", borderRadius: 6, padding: "5px 7px", cursor: "pointer", color: "var(--text-3)", display: "inline-flex" }}>
            <ChevronRight style={{ width: 13, height: 13 }} />
          </button>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: "var(--navy)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
            {initials(lead.clinic)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#5B6CFF" }}>Lead profile</span>
              <span style={{ padding: "1px 6px", borderRadius: 4, fontSize: 8, fontWeight: 700, letterSpacing: "0.05em", background: "var(--subtle)", color: "var(--text-3)" }}>{stats.stage}{stats.subStatus ? ` · ${stats.subStatus}` : ""}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} data-testid="lp-clinic-name">{lead.clinic}</div>
            <div style={{ fontSize: 11, color: "var(--text-3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {primary?.name || "—"}{primary?.role ? ` · ${primary.role}` : ""}
            </div>
          </div>
          <button data-testid="lp-open-full" onClick={() => onOpenLead?.(lead)} title="Open in Lead Focus"
            style={{ background: "none", border: "0.5px solid var(--border)", borderRadius: 6, padding: "5px 7px", cursor: "pointer", color: "var(--blue)", display: "inline-flex" }}>
            <ExternalLink style={{ width: 13, height: 13 }} />
          </button>
          <button data-testid="lp-close" onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", padding: 4 }}>
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Tabs */}
        <div role="tablist" style={{ display: "flex", borderBottom: "0.5px solid var(--border-soft)", flexShrink: 0 }}>
          {[
            { id: "snapshot", label: "Snapshot", Icon: TrendingUp },
            { id: "talking",  label: "Talking pts", Icon: Sparkles },
            { id: "activity", label: "Activity", Icon: ActivityIcon },
            { id: "edits",    label: "Quick edit", Icon: Edit3 },
          ].map((t) => {
            const Icon = t.Icon;
            const active = tab === t.id;
            return (
              <button key={t.id} role="tab" data-testid={`lp-tab-${t.id}`} onClick={() => setTab(t.id)}
                style={{
                  flex: 1, padding: "10px 6px", background: active ? "var(--blue-soft)" : "transparent",
                  border: "none", borderBottom: active ? "2px solid var(--blue)" : "2px solid transparent",
                  fontSize: 11, fontWeight: 500, fontFamily: "inherit", cursor: "pointer",
                  color: active ? "var(--blue)" : "var(--text-3)",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5,
                }}>
                <Icon style={{ width: 11, height: 11 }} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 14 }}>
          {tab === "snapshot" && (
            <div data-testid="lp-body-snapshot" style={{ display: "flex", flexDirection: "column", gap: 12 }}>

              {/* Hero score row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                <Tile label="AI score" value={`${stats.aiScore}/100`} tone={stats.aiScore >= 71 ? "green" : stats.aiScore >= 41 ? "amber" : "gray"} testid="lp-ai-score" />
                <Tile label="Tier"  value={stats.tier} tone={stats.tier === "A" ? "purple" : stats.tier === "B" ? "blue" : "gray"} testid="lp-tier" />
                <Tile label="Rating" value={stats.rating || "—"} tone={stats.rating === "Hot" ? "red" : stats.rating === "Warm" ? "amber" : "gray"} testid="lp-rating" />
              </div>

              {/* AI fit breakdown */}
              {(stats.fit.ccm || stats.fit.rpm || stats.fit.apcm || stats.fit.awv || stats.fit.ehr || stats.fit.rcm) && (
                <Section title="AI product fit" icon={Sparkles}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4 }}>
                    {[["CCM", stats.fit.ccm], ["RPM", stats.fit.rpm], ["APCM", stats.fit.apcm], ["AWV", stats.fit.awv], ["EHR", stats.fit.ehr], ["RCM", stats.fit.rcm]].filter(([, v]) => v != null).map(([k, v]) => (
                      <FitBar key={k} label={k} value={v} testid={`lp-fit-${k.toLowerCase()}`} />
                    ))}
                  </div>
                </Section>
              )}

              {/* Practice vitals */}
              <Section title="Practice vitals" icon={Building2}>
                <KvRow icon={Stethoscope} label="Specialty" value={stats.specialty} sub={stats.subSpecialty} />
                <KvRow icon={Users}        label="Providers" value={`${stats.providers} provider${stats.providers === 1 ? "" : "s"}`} sub={stats.taxonomy} />
                {stats.patients != null && <KvRow icon={UserIcon} label="Active patients" value={fmtNum(stats.patients)} />}
                {stats.avgAge != null && <KvRow icon={CalendarIcon} label="Avg patient age" value={`${stats.avgAge}${stats.femalePct ? `  · ${stats.femalePct}% female` : ""}`} />}
              </Section>

              {/* Medicare panel */}
              <Section title="Medicare panel" icon={DollarSign}>
                <KvRow label="Benes" value={fmtNum(stats.benes)} sub={stats.medicareAdvantage != null ? `${stats.medicareAdvantage}% MA` : null} />
                <KvRow label="Allowed (YTD)" value={fmtUSD(stats.allowed)} />
                {stats.revenueEstimate != null && (
                  <KvRow label="Est. CCM ARR" value={fmtUSD(stats.revenueEstimate)} tone="green" sub="If 35% of benes enroll" />
                )}
              </Section>

              {/* Top HCPCS list */}
              {stats.top1 && (
                <Section title="Top HCPCS codes" icon={Award}>
                  {[stats.top1, stats.top2, stats.top3].filter(Boolean).map((c, i) => (
                    <KvRow key={i} label={c.code} value={fmtUSD(c.paid || 0)} sub={c.desc?.slice(0, 56)} />
                  ))}
                </Section>
              )}

              {/* EHR / PMS */}
              <Section title="EHR / PMS" icon={Cpu}>
                <KvRow label="Vendor" value={stats.ehrVendor} />
                <KvRow label="Product" value={stats.ehrProduct} sub={stats.ehrAge != null ? `${stats.ehrAge} yrs on system` : null} />
              </Section>

              {/* Pipeline status */}
              <Section title="Pipeline status" icon={Briefcase}>
                <KvRow label="Stage" value={stats.stage + (stats.subStatus ? ` · ${stats.subStatus}` : "")} />
                <KvRow label="Source" value={stats.source} />
                {stats.owner && <KvRow label="Owner" value={stats.owner} />}
                {stats.daysInPipeline != null && <KvRow label="Days in pipeline" value={`${stats.daysInPipeline}d`} />}
                {stats.daysSinceTouch != null && (
                  <KvRow label="Last touch" value={`${stats.daysSinceTouch}d ago`} tone={stats.daysSinceTouch > 14 ? "red" : stats.daysSinceTouch > 7 ? "amber" : "green"} />
                )}
                {stats.nextAction?.text && (
                  <KvRow label="Next action" value={stats.nextAction.text} sub={stats.nextAction.when} />
                )}
              </Section>

              {/* Qualification snapshot */}
              {(stats.painPoint || stats.budgetRange || stats.decisionTimeline) && (
                <Section title="Qualification" icon={Star}>
                  {stats.painPoint && <KvRow label="Pain point" value={String(stats.painPoint).split(";")[0]} />}
                  {stats.budgetRange && <KvRow label="Budget" value={stats.budgetRange} />}
                  {stats.decisionTimeline && <KvRow label="Decision timeline" value={stats.decisionTimeline} />}
                </Section>
              )}

              {/* Primary location */}
              {locations[0] && (
                <Section title="Practice address" icon={MapPin}>
                  <div style={{ fontSize: 11, color: "var(--text-2)", lineHeight: 1.55 }}>
                    {locations[0].address || "—"}<br/>
                    {locations[0].city || "—"}, {locations[0].state || "—"} {locations[0].zip || ""}
                  </div>
                </Section>
              )}
            </div>
          )}

          {tab === "talking" && (
            <div data-testid="lp-body-talking" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Section title="AI talking points">
                {(lead.ai_insights || [
                  `Anchor on top HCPCS code ${stats.top1?.code || "G0438"} (paid ${fmtUSD(stats.top1?.paid || 0)} YTD).`,
                  `Highlight CCM revenue at ~$76/bene/month — your panel could add ${fmtUSD(stats.revenueEstimate || 0)} ARR.`,
                  `Reference ${stats.ehrVendor} pain point if integration cycle came up in past notes.`,
                ]).map((p, i) => (
                  <div key={i} data-testid={`lp-talking-${i}`} style={{ padding: "8px 10px", background: "var(--blue-soft)", borderRadius: 8, fontSize: 11, color: "var(--text-2)", lineHeight: 1.5, marginBottom: 6 }}>
                    <Sparkles style={{ width: 10, height: 10, color: "var(--blue)", marginRight: 5, verticalAlign: "middle" }} />
                    {p}
                  </div>
                ))}
              </Section>

              <Section title="Objection handlers">
                {[
                  { obj: "We're happy with our current EHR.",
                    reply: "Most reps say that until they see our 47-day go-live + zero migration cost on greenfield installs." },
                  { obj: "Pricing seems high.",
                    reply: "Cost recovery from coding lift is typically inside 2.3 months for practices your size." },
                  { obj: "Our patients won't use it.",
                    reply: "Adoption averages 78% in the first 90 days — driven by the auto-enrollment workflow, not pull-marketing." },
                ].map((o, i) => (
                  <div key={i} data-testid={`lp-objection-${i}`} style={{ padding: "8px 10px", background: "var(--subtle)", borderRadius: 8, marginBottom: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-3)", marginBottom: 3 }}>OBJECTION</div>
                    <div style={{ fontSize: 11, color: "var(--text-2)", lineHeight: 1.5, marginBottom: 6 }}>{o.obj}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: "#1D9E75", marginBottom: 3 }}>REPLY</div>
                    <div style={{ fontSize: 11, color: "var(--text-2)", lineHeight: 1.5 }}>{o.reply}</div>
                  </div>
                ))}
              </Section>
            </div>
          )}

          {tab === "activity" && (
            <div data-testid="lp-body-activity" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Section title="Recent activity">
                {(lead.activity || []).length === 0 ? (
                  <div style={{ padding: "12px", fontSize: 11, color: "var(--text-3)", textAlign: "center" }}>No prior activity recorded.</div>
                ) : (lead.activity.slice(0, 10).map((a, i) => (
                  <div key={i} data-testid={`lp-activity-${i}`} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 0", borderBottom: i < Math.min(9, lead.activity.length - 1) ? "0.5px solid var(--border-soft)" : "none" }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", marginTop: 6, flexShrink: 0, background: a.kind === "email" ? "var(--blue)" : a.kind === "call" ? "#1D9E75" : a.kind === "stage" ? "#A6660B" : "var(--text-3)" }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: "var(--text-2)", lineHeight: 1.45 }}>{a.text}</div>
                      <div style={{ fontSize: 9, color: "var(--text-4)", marginTop: 1 }}>{a.by} · {new Date(a.at).toLocaleDateString()}</div>
                    </div>
                    <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.05em", color: "var(--text-3)", textTransform: "uppercase" }}>{a.kind}</span>
                  </div>
                )))}
              </Section>

              <Section title="Notes">
                {(lead.notes || []).slice(0, 5).map((n) => (
                  <div key={n.id} style={{ padding: "8px 10px", background: "var(--subtle)", borderRadius: 8, marginBottom: 6 }}>
                    <div style={{ fontSize: 9, color: "var(--text-3)", marginBottom: 3 }}>{n.by} · {n.at}</div>
                    <div style={{ fontSize: 11, color: "var(--text-2)", lineHeight: 1.5 }}>{n.body}</div>
                  </div>
                ))}
                {(!lead.notes || lead.notes.length === 0) && (
                  <div style={{ padding: "12px", fontSize: 11, color: "var(--text-3)", textAlign: "center" }}>No notes yet.</div>
                )}
              </Section>
            </div>
          )}

          {tab === "edits" && (
            <div data-testid="lp-body-edits" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <QuickEditLeadFields lead={lead} onLeadPatched={(patch) => onChangeLead?.({ ...lead, ...patch })} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "10px 12px", borderTop: "0.5px solid var(--border-soft)", display: "flex", gap: 6, flexShrink: 0, background: "var(--subtle)" }}>
          {/* Iter 192d (Priyanka — RC June 23 video):
              The "Already on a call with this lead" toast was a misleading
              fallback that fired any time the parent forgot to wire onCall.
              Even after wiring SalesDialerLive's onCall in iter 192c,
              Priyanka still saw the toast in production — which means
              either a stale build or a render path we don't control. The
              fix below removes the fallback entirely: clicking Call now
              ALWAYS dispatches `webphone:dial` directly to the global
              WebPhone dock so the call is placed unconditionally, even if
              no parent handler is wired. The dock then surfaces the
              dialpad / mute / hold / end controls.

              We also clear the parent's auto-dismiss flag (when provided)
              via the optional onCall prop so the dialer page can re-show
              the active-call card it was suppressing. */}
          <button
            data-testid="lp-action-call"
            onClick={() => {
              try {
                // Always invoke the parent handler if wired so it can
                // transition the dialer page state machine to "ringing".
                if (typeof onCall === "function") {
                  onCall(lead);
                  return;
                }
                // Fallback: place the call directly via the global
                // WebPhone dock. Resolve the best phone we can find.
                const primary = (lead?.contacts && lead.contacts[0]) || null;
                const number = primary?.phone || lead?.phone || lead?.primary_phone || "";
                if (!number) {
                  toast.error("No phone number on this lead");
                  return;
                }
                window.dispatchEvent(new CustomEvent("webphone:dial", {
                  detail: {
                    number,
                    leadId: lead?.id,
                    leadName: lead?.clinic || lead?.name || "",
                  },
                }));
                toast.info(`Dialing ${number}…`);
              } catch (e) {
                console.warn("[lp-action-call] failed to place call:", e?.message);
                toast.error("Couldn't place the call. Open the dialer and try again.");
              }
            }}
            style={actionBtn("var(--success)")}
          >
            <Phone style={{ width: 11, height: 11 }} /> Call
          </button>
          <button
            data-testid="lp-action-email"
            onClick={() => {
              if (typeof onEmail === "function") {
                onEmail(lead);
                // Email composer opens a separate modal; safe to
                // close the slide-in once it's open.
                setTimeout(() => onClose?.(), 50);
              } else {
                toast.info("Open email composer (mock)");
              }
            }}
            style={actionBtn("var(--blue)")}
          >
            <Mail style={{ width: 11, height: 11 }} /> Email
          </button>
          <button
            data-testid="lp-action-followup"
            onClick={() => {
              if (typeof onFollowUp === "function") {
                onFollowUp(lead);
              } else {
                toast.info("Follow-up task added (mock)");
              }
            }}
            style={actionBtn("#A6660B")}
          >
            <Clock style={{ width: 11, height: 11 }} /> Follow-up
          </button>
        </div>
      </aside>

      <style>{`
        @keyframes lp-slide { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes lp-fade  { from { opacity: 0; }                 to { opacity: 1; } }
      `}</style>
    </>
  );
}

/* ── Tiny helpers ─────────────────────────────────────────── */
function Tile({ label, value, tone = "blue", small = false, testid }) {
  const map = {
    blue:   { bg: "var(--blue-soft, #EEF2FF)",  color: "var(--navy)" },
    green:  { bg: "#E9F8F1",                    color: "#0F6E56" },
    amber:  { bg: "#FFF7E0",                    color: "#A6660B" },
    red:    { bg: "#FCEBEB",                    color: "#A32D2D" },
    purple: { bg: "#F4EAFE",                    color: "#5B6CFF" },
    gray:   { bg: "var(--subtle)",              color: "var(--text-2)" },
  };
  const c = map[tone] || map.blue;
  return (
    <div data-testid={testid} style={{ padding: "8px 10px", background: c.bg, borderRadius: 8, minWidth: 0 }}>
      <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: c.color, opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: small ? 11 : 14, fontWeight: 700, color: c.color, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
    </div>
  );
}

function FitBar({ label, value, testid }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const tone = v >= 70 ? "#0F6E56" : v >= 40 ? "#A6660B" : "var(--text-3)";
  const bg   = v >= 70 ? "#E9F8F1" : v >= 40 ? "#FFF7E0" : "var(--subtle)";
  return (
    <div data-testid={testid} style={{ padding: "6px 8px", borderRadius: 6, background: bg, minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: tone, letterSpacing: "0.04em" }}>{label}</span>
        <span style={{ fontSize: 9, fontWeight: 700, color: tone }}>{v}</span>
      </div>
      <div style={{ height: 3, borderRadius: 2, background: "rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <div style={{ width: `${v}%`, height: "100%", background: tone, transition: "width 240ms ease" }} />
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children, trailing }) {
  return (
    <section>
      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 8 }}>
        {Icon ? <Icon style={{ width: 11, height: 11, color: "var(--text-3)" }} /> : null}
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-3)", flex: 1 }}>{title}</span>
        {trailing}
      </div>
      {children}
    </section>
  );
}

function KvRow({ icon: Icon, label, value, sub, tone }) {
  const toneColor = tone === "red" ? "#A32D2D" : tone === "amber" ? "#A6660B" : tone === "green" ? "#0F6E56" : "var(--text-2)";
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "5px 0", fontSize: 11, borderBottom: "0.5px dashed var(--border-soft)" }}>
      {Icon && <Icon style={{ width: 11, height: 11, color: "var(--text-4)", marginTop: 2, flexShrink: 0 }} />}
      <span style={{ color: "var(--text-3)", flex: "0 0 88px", textTransform: "capitalize" }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, color: toneColor, fontWeight: 500, textAlign: "right" }}>
        <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
        {sub && <div style={{ fontSize: 10, color: "var(--text-4)", fontWeight: 400, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>}
      </span>
    </div>
  );
}

function EditRow({ label, value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 0", borderBottom: "0.5px solid var(--border-soft)" }}>
      <span style={{ width: 58, fontSize: 9, color: "var(--text-3)", flexShrink: 0, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
      {editing ? (
        <>
          <input
            data-testid={`${testid}-input`}
            className="input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { onSave(draft); setEditing(false); } if (e.key === "Escape") setEditing(false); }}
            style={{ flex: 1, fontSize: 11, padding: "3px 6px", minWidth: 0 }}
            autoFocus
          />
          <button onClick={() => { onSave(draft); setEditing(false); }} data-testid={`${testid}-save`}
            style={{ background: "var(--success)", color: "#fff", border: "none", borderRadius: 4, padding: "3px 5px", cursor: "pointer", display: "inline-flex" }}>
            <Check style={{ width: 10, height: 10 }} />
          </button>
        </>
      ) : (
        <>
          <span style={{ flex: 1, fontSize: 11, color: value ? "var(--text-2)" : "var(--text-4)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{value || "—"}</span>
          <button data-testid={testid} onClick={() => setEditing(true)}
            style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", padding: 2 }}>
            <Edit3 style={{ width: 10, height: 10 }} />
          </button>
        </>
      )}
    </div>
  );
}

const actionBtn = (color) => ({
  flex: 1, padding: "6px 10px",
  border: `0.5px solid ${color}`,
  borderRadius: 6, background: "#fff", color,
  fontSize: 11, fontWeight: 500, fontFamily: "inherit", cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5,
});

const addBtnStyle = {
  background: "none", border: "0.5px solid var(--border)", borderRadius: 4,
  padding: "3px 7px", fontSize: 9, fontWeight: 500, color: "var(--blue)", cursor: "pointer",
  fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 3,
};
const trashBtnStyle = {
  marginLeft: "auto", background: "none", border: "none", color: "var(--text-3)",
  cursor: "pointer", padding: 2, display: "inline-flex",
};
const contactRowStyle = {
  padding: "8px 10px", background: "var(--subtle)", borderRadius: 8,
  marginBottom: 6, border: "0.5px solid var(--border-soft)",
};
const emptyRowStyle = {
  padding: "10px 12px", fontSize: 11, color: "var(--text-3)",
  textAlign: "center", border: "0.5px dashed var(--border)", borderRadius: 8,
};
